import { apiUrl } from '../platform/api-origin';

export const EVENT_NAMES = [
  // 표준 4이벤트. 동의 이전에도 나가며 개인을 식별하지 않는다.
  // 정본: workspace/docs/munops/ops/release-integrity.md 「표준 계측 이벤트」
  'app_open', 'landing_view', 'activation_start', 'activation_done',
  'public_page_view', 'return_visit', 'share_done', 'purchase_intent', 'departure_ready',
  'landing_viewed', 'planner_started', 'trip_created', 'allowance_confirmed', 'items_selected',
  'auto_place_completed', 'placement_adjusted', 'first_manifest_created',
  'manifest_exported', 'repack_opened', 'post_trip_reviewed', 'trip_cloned',
  'trip_rule_confirmed', 'bag_profile_saved', 'departure_check_updated',
  'readiness_viewed', 'packing_status_changed', 'bag_assignment_changed',
  'schedule_updated', 'document_status_changed', 'packing_quantity_changed',
  'trip_preset_applied', 'participant_updated', 'memory_updated', 'photo_added',
  'share_created', 'share_sync_succeeded', 'share_sync_failed', 'share_revoked', 'today_viewed',
  'premium_offer_viewed', 'purchase_started', 'purchase_pending', 'purchase_failed',
  'purchase_paid_ungranted', 'purchase_granted', 'purchase_restore_started',
  'purchase_restored', 'purchase_refunded', 'purchase_chargeback',
  'purchase_provider_outage', 'purchase_reconciliation_matched',
  'purchase_reconciliation_mismatch', 'purchase_kill_switch_seen', 'storage_failed',
] as const;

export type EventName = typeof EVENT_NAMES[number];
type AllowedValue = string | number | boolean;

const ALLOWED_PROPERTIES = new Set([
  'channel', 'buildVersion', 'viewportClass', 'locale', 'bagCountBucket',
  'itemCountBucket', 'elapsedBucket', 'resultCode', 'schemaVersion', 'trafficClass', 'entrySource',
]);

export interface AnalyticsEvent {
  name: EventName;
  occurredAt: string;
  schemaVersion: 1;
  properties: Record<string, AllowedValue>;
}

export function parseAnalyticsEvent(input: unknown): AnalyticsEvent | null {
  if (!input || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (typeof candidate.name !== 'string' || !EVENT_NAMES.includes(candidate.name as EventName)) return null;
  if (candidate.schemaVersion !== 1 || typeof candidate.occurredAt !== 'string' || Number.isNaN(Date.parse(candidate.occurredAt))) return null;
  if (!candidate.properties || typeof candidate.properties !== 'object' || Array.isArray(candidate.properties)) return null;
  const properties = candidate.properties as Record<string, unknown>;
  if (Object.keys(properties).some((key) => !ALLOWED_PROPERTIES.has(key))) return null;
  if (properties.trafficClass !== undefined && !['qa', 'public', 'unclassified'].includes(String(properties.trafficClass))) return null;
  if (properties.entrySource !== undefined && !['family', 'share', 'search', 'direct', 'other'].includes(String(properties.entrySource))) return null;
  if (Object.values(properties).some((value) => !['string', 'number', 'boolean'].includes(typeof value))) return null;
  return {
    name: candidate.name as EventName,
    occurredAt: candidate.occurredAt,
    schemaVersion: 1,
    properties: properties as Record<string, AllowedValue>,
  };
}

export function sanitizeEvent(name: EventName, input: Record<string, unknown>): AnalyticsEvent {
  const properties: Record<string, AllowedValue> = {};
  for (const [key, value] of Object.entries(input)) {
    if (ALLOWED_PROPERTIES.has(key) && ['string', 'number', 'boolean'].includes(typeof value)) properties[key] = value as AllowedValue;
  }
  return { name, occurredAt: new Date().toISOString(), schemaVersion: 1, properties };
}

/**
 * 동의 이전에도 나가는 표준 4이벤트.
 *
 * 왜 동의 게이트 밖인가 — 이 제품의 분석 동의는 설정 화면 깊은 곳의 opt-in(기본 off)이다.
 * 그래서 실측(2026-08-11)에서 `landing_viewed`가 App.tsx에 배선돼 있는데도 첫 방문 계측
 * 요청이 **0건**이었다. 아무도 켜지 않는 스위치 뒤에 퍼널 분모를 두면 측정은 없는 것과 같고,
 * /pulse가 판정할 표본이 영원히 생기지 않는다.
 *
 * 이 tier가 보내는 것은 **개인을 식별하지 않는 카운터**뿐이다.
 * - 기기에 식별자를 쓰거나 읽지 않는다(쿠키·localStorage 식별자 없음)
 * - 여행·물건·치수·무게·주소·사진·locale은 보내지 않는다
 * - 보내는 값은 채널·빌드·뷰포트 등급뿐이다
 *
 * 물건 이름·수량 버킷·결제 흐름 같은 **제품 상세 이벤트는 `track()`에 그대로 남아
 * 동의 뒤에만** 나간다. 두 tier를 섞지 않는다.
 */
const STANDARD_EVENT_NAMES = new Set<EventName>(['app_open', 'landing_view', 'activation_start', 'activation_done', 'public_page_view', 'return_visit', 'share_done', 'purchase_intent', 'departure_ready']);

function viewportClass(): string {
  if (typeof window === 'undefined') return 'unknown';
  return window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1280 ? 'tablet' : 'wide';
}

function post(event: AnalyticsEvent) {
  try {
    void fetch(apiUrl('/api/events'), {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ events: [event] }),
    })
      // 응답 본문을 읽지 않으면 요청이 열린 채 남아 브라우저 networkidle이 오지 않는다.
      .then((response) => response.arrayBuffer())
      .catch(() => undefined);
  } catch {
    // 계측 실패가 제품 동작을 막으면 안 된다.
  }
}

/** Only coarse counters: no referrer URL, search text, trip id or person identifier. */
function attribution() {
  let trafficClass = typeof location !== 'undefined' && (new URLSearchParams(location.search).get('qa') === '1' || new URLSearchParams(location.search).get('traffic') === 'synthetic') ? 'qa' : 'public';
  let entrySource = 'direct';
  try {
    if (trafficClass === 'qa') sessionStorage.setItem('carryonpan.qa', '1');
    trafficClass = sessionStorage.getItem('carryonpan.qa') === '1' ? 'qa' : trafficClass;
    const saved = sessionStorage.getItem('carryonpan.entrySource');
    if (saved && ['family', 'share', 'search', 'direct', 'other'].includes(saved)) entrySource = saved;
    else {
      if (new URLSearchParams(location.search).get('from') === 'search') entrySource = 'search';
      else if (location.pathname === '/family-packing') entrySource = 'family';
      else if (location.pathname.startsWith('/shared/')) entrySource = 'share';
      else if (document.referrer) {
        const host = new URL(document.referrer).hostname;
        entrySource = /(^|\.)(google\.[a-z.]+|naver\.com|bing\.com|daum\.net)$/.test(host) ? 'search' : 'other';
      }
      sessionStorage.setItem('carryonpan.entrySource', entrySource);
    }
  } catch { /* Counters still work when session storage is denied. */ }
  return { trafficClass, entrySource };
}

/** Keep legacy blob1 bytes; append closed metadata for the existing durable reader. */
export function analyticsDataPoint(event: AnalyticsEvent) {
  const channel = typeof event.properties.channel === 'string' && ['web', 'apps_in_toss', 'ios', 'android'].includes(event.properties.channel) ? event.properties.channel : 'unknown';
  const build = typeof event.properties.buildVersion === 'string' && /^[a-f0-9]{7,40}$/.test(event.properties.buildVersion) ? event.properties.buildVersion : 'unknown';
  const traffic = event.properties.trafficClass === 'qa' ? 'qa' : event.properties.trafficClass === 'public' ? 'public' : 'unclassified';
  const source = typeof event.properties.entrySource === 'string' && ['family', 'share', 'search', 'direct', 'other'].includes(event.properties.entrySource) ? event.properties.entrySource : 'unknown';
  return { blobs: [JSON.stringify(event), event.name, channel, build, traffic, source] };
}

export function trackStandard(name: EventName, properties: Record<string, unknown> = {}) {
  if (!STANDARD_EVENT_NAMES.has(name)) return;
  post(sanitizeEvent(name, {
    channel: document.documentElement.dataset.channel ?? 'web',
    buildVersion: __BUILD_VERSION__,
    viewportClass: viewportClass(),
    ...attribution(),
    ...properties,
  }));
}

/** 같은 방문에서 한 번만 보낸다. 식별자를 만들지 않으므로 sessionStorage 플래그만 쓴다. */
export function trackStandardOnce(name: EventName) {
  const key = `carryonpan.beacon.${name}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // Private mode 등으로 sessionStorage를 못 쓰면 중복을 감수하고 보낸다 — 0건보다 낫다.
  }
  trackStandard(name);
}

export function track(name: EventName, properties: Record<string, unknown> = {}) {
  if (localStorage.getItem('carryonpan.analyticsConsent') !== 'yes') return;
  post(sanitizeEvent(name, { channel: document.documentElement.dataset.channel ?? 'web', buildVersion: __BUILD_VERSION__, locale: navigator.language, ...attribution(), ...properties }));
}
